/**
 * Keyboard navigation for the answer sliders.
 *
 * With a hundred-odd questions on one page, reaching for the mouse between
 * each one is most of the work. These bindings let the whole form be answered
 * from the home row:
 *
 *   ↑ / ↓     previous / next question
 *   ← / →     move that question's slider
 *   +         mark it very important
 *   -         mark it not very important
 *   *         back to normal importance
 *
 * Left and right are deliberately *not* intercepted: moving the highlight also
 * moves DOM focus onto that question's range input, and a focused range already
 * handles ← / → (and Home / End, and right-to-left layouts) correctly. Handling
 * them here as well would double every step.
 *
 * Up and down *are* intercepted, because a focused range treats them as
 * synonyms for left and right — so without preventDefault, moving between
 * questions would silently change the answer you were leaving.
 *
 * SELECTION IS ALWAYS FOCUS
 * -------------------------
 * Every route into a question goes through `select`: ↑/↓, clicking a row, and
 * the page opening an area from its nav. They all end with that row's range
 * input focused, so ← / → work immediately afterwards without a further click.
 * A highlight that isn't focus is a highlight the arrows ignore.
 *
 * Walking off the end of an open area opens the next one and lands on its first
 * question (↑ on the previous one's last). That's why `order` spans closed
 * areas too, and why selection can outrun the DOM — the row is mounted by a
 * state change, so focusing it has to wait a render.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import type { Importance } from "@/shared/scale";

export interface KeyboardNavOptions {
  /**
   * Every question id in page order, including those inside collapsed areas.
   * Walking past the end of one area is what opens the next, so the closed
   * ones have to be in the walk rather than filtered out of it.
   */
  order: string[];
  /** Whether a given question currently carries an answer. */
  isAnswered: (questionId: string) => boolean;
  onImportance: (questionId: string, importance: Importance) => void;
  /**
   * Open whatever collapsed area holds this question, so its row mounts. Called
   * before the selection is applied; the focus that follows waits for the row.
   */
  ensureVisible?: (questionId: string) => void;
}

/** How a selection should treat scrolling. */
interface SelectOptions {
  /**
   * Whether to scroll the row into view. Off when something else is already
   * scrolling — a nav anchor's jump, say, which a reveal here would fight.
   */
  scroll?: boolean;
}

/** Breathing room kept between the highlighted row and the usable viewport edges. */
const GAP = 24;

/**
 * Scroll the highlighted row fully into view, the minimum distance needed.
 *
 * `scrollIntoView({ block: "nearest" })` almost does this, but not quite: the
 * sticky app bar covers the top of the viewport and the browser knows nothing
 * about it, and when *both* edges of a row overflow at once — a tall prompt in
 * a short window — the spec says to do nothing at all, leaving the row cut off
 * by whichever edge it arrived from. So the band is measured here instead.
 *
 * When the row genuinely can't fit, its top wins: the prompt is the part you
 * need to read before answering, and the slider is usable while its labels are
 * still below the fold.
 */
function reveal(row: HTMLElement) {
  // The bar is `sticky top-0`, so once the page has scrolled at all its bottom
  // edge *is* the top of the usable area; before that it sits in flow and the
  // same measurement still holds.
  const bar = document.querySelector<HTMLElement>("[data-app-bar]");
  const top = (bar ? Math.max(0, bar.getBoundingClientRect().bottom) : 0) + GAP;
  const bottom = window.innerHeight - GAP;

  const rect = row.getBoundingClientRect();
  // Positive scrolls the page down (content moves up).
  let delta = 0;
  if (rect.bottom > bottom) delta = rect.bottom - bottom;
  // Bring the tail up, but never at the cost of pushing the head under the bar.
  if (rect.top - delta < top) delta = rect.top - top;
  if (delta === 0) return;

  // "instant", not "auto". `auto` defers to CSS, and globals.css sets
  // `html { scroll-behavior: smooth }` for anchor links — so `auto` here starts
  // a smooth scroll that the next keypress interrupts and restarts. Held arrows
  // then never arrive anywhere and the highlight drifts off screen entirely.
  window.scrollBy({ top: delta, behavior: "instant" as ScrollBehavior });
}

/** True when the keystroke belongs to whatever the user is typing into. */
function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName;
  if (tag === "TEXTAREA" || tag === "SELECT") return true;
  // Range inputs are ours to drive; every other input is somebody typing.
  return tag === "INPUT" && (el as HTMLInputElement).type !== "range";
}

export function useSliderKeyboard({
  order,
  isAnswered,
  onImportance,
  ensureVisible,
}: KeyboardNavOptions) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const inputs = useRef(new Map<string, HTMLInputElement>());
  /** A selection whose row hasn't mounted yet — see `select` below. */
  const pending = useRef<{ id: string; scroll: boolean } | null>(null);

  /**
   * Stable for the lifetime of the hook, so the ~140 memoised sliders holding
   * it never see a changed prop. A factory returning a fresh closure per
   * question would re-render every row on every keystroke.
   */
  const register = useCallback((questionId: string, el: HTMLInputElement | null) => {
    if (el) inputs.current.set(questionId, el);
    else inputs.current.delete(questionId);
  }, []);

  // Read through refs so the listener can be attached once rather than being
  // torn down and rebuilt every time an answer changes.
  const latest = useRef({ order, isAnswered, onImportance, ensureVisible, activeId });
  latest.current = { order, isAnswered, onImportance, ensureVisible, activeId };

  /**
   * Give the row keyboard focus and bring it into view.
   *
   * Focusing is the whole point: ← / → are left to the browser, so they only
   * move a slider once that slider is the focused element. A row that is
   * merely highlighted answers nothing.
   *
   * Reveal the row, not the input inside it. The input alone can sit
   * comfortably in view while the prompt above it — the part you actually need
   * to read — is still cut off by the viewport edge or hidden under the app bar.
   */
  const settle = useCallback((id: string, scroll: boolean): boolean => {
    const el = inputs.current.get(id);
    if (!el) return false;
    el.focus({ preventScroll: true });
    if (scroll) reveal(el.closest<HTMLElement>("[data-question-row]") ?? el);
    return true;
  }, []);

  /**
   * Select a question, opening its area first if it is closed.
   *
   * Opening is a state change, so the row it mounts doesn't exist yet when this
   * returns — nothing to focus. The intent is parked in `pending` and the
   * effect below picks it up on the render that mounts the row.
   */
  const select = useCallback(
    (id: string, { scroll = true }: SelectOptions = {}) => {
      setActiveId(id);
      latest.current.ensureVisible?.(id);
      pending.current = settle(id, scroll) ? null : { id, scroll };
    },
    [settle],
  );

  // Deliberately un-deped: it runs after every commit, which is the only way to
  // catch the one that finally mounts a pending row.
  useEffect(() => {
    const p = pending.current;
    if (p && settle(p.id, p.scroll)) pending.current = null;
  });

  useEffect(() => {
    const move = (delta: 1 | -1) => {
      const { order: ids, activeId: current } = latest.current;
      if (ids.length === 0) return;

      const at = current ? ids.indexOf(current) : -1;
      // Nothing selected yet: ↓ starts at the top, ↑ starts at the bottom.
      const next =
        at === -1
          ? delta === 1
            ? 0
            : ids.length - 1
          : Math.min(ids.length - 1, Math.max(0, at + delta));

      const id = ids[next];
      if (!id) return;
      select(id);
    };

    const setImportance = (importance: Importance) => {
      const { activeId: current, isAnswered: answered, onImportance: apply } =
        latest.current;
      // Importance without an answer would weight nothing.
      if (!current || !answered(current)) return;
      apply(current, importance);
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          move(1);
          break;
        case "ArrowUp":
          e.preventDefault();
          move(-1);
          break;
        // "+" needs shift on most layouts, so accept the unshifted key too.
        case "+":
        case "=":
          e.preventDefault();
          setImportance(1);
          break;
        case "-":
        case "_":
          e.preventDefault();
          setImportance(-1);
          break;
        case "*":
          e.preventDefault();
          setImportance(0);
          break;
        default:
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // `select` is stable, so this still attaches exactly once.
  }, [select]);

  return { activeId, select, register };
}
